import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { updateMe, formatApiError, fetchMySubscription, cancelSubscription } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";
import { cacheClear } from "@/lib/cache";
import { listTimezones, detectTimezone } from "@/lib/time";
import { toast } from "sonner";
import { User, Globe, Clock, Crown, Lightning, Files as Receipt, ArrowUpRight, X } from "@phosphor-icons/react";
import QuotaBadge from "@/components/QuotaBadge";

export default function ProfilePage() {
  const { user, refreshUser, isAdmin } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [country, setCountry] = useState(user?.country || "US");
  const [tz, setTz] = useState(user?.timezone || detectTimezone());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sub, setSub] = useState(null);
  const [history, setHistory] = useState([]);
  const [cancelling, setCancelling] = useState(false);

  const timezones = listTimezones();

  useEffect(() => {
    let alive = true;
    fetchMySubscription()
      .then((d) => {
        if (!alive) return;
        setSub(d.subscription);
        setHistory(d.history || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user]);

  const onSave = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await updateMe({ name, country, timezone: tz });
      cacheClear("news_");
      await refreshUser();
      toast.success("Profile updated");
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of your billing period.")) return;
    setCancelling(true);
    try {
      await cancelSubscription();
      toast.success("Subscription cancelled");
      await refreshUser();
      const fresh = await fetchMySubscription();
      setSub(fresh.subscription);
      setHistory(fresh.history || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelling(false);
    }
  };

  const inputClass = "w-full bg-slate-50 border border-slate-200 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 px-3 py-3 text-sm rounded-lg text-slate-900";
  const labelClass = "flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2";

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12">
      <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
        / Profile
      </span>
      <h1 className="font-bold text-4xl md:text-5xl mt-3 mb-10 text-slate-900">
        {user?.name || "Your profile"}
      </h1>

      <div className="border border-slate-200 bg-white p-6 md:p-8 mb-8 rounded-xl" data-testid="profile-summary">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-14 h-14 rounded-full border border-slate-200" />
            ) : (
              <div className="w-14 h-14 bg-blue-600 flex items-center justify-center font-bold text-2xl text-white rounded-full">
                {(user?.name?.[0] || user?.email?.[0] || "U").toUpperCase()}
              </div>
            )}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Email</div>
              <div className="font-bold text-lg text-slate-900">{user?.email}</div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mt-2">Plan</div>
              <div className="text-sm flex items-center gap-2">
                {isAdmin ? (
                  <span className="text-blue-600 flex items-center gap-1"><Crown size={12} weight="fill" /> Admin · Unlimited</span>
                ) : user?.is_premium ? (
                  <span className="text-blue-600 flex items-center gap-1"><Crown size={12} weight="fill" /> Premium · Unlimited</span>
                ) : (
                  <span className="text-slate-600 flex items-center gap-1"><Lightning size={12} /> Free · 5 generations/day</span>
                )}
              </div>
            </div>
          </div>
          <QuotaBadge variant="banner" />
        </div>
      </div>

      {!isAdmin && !user?.is_premium && (
        <div id="upgrade" className="border border-blue-200 bg-blue-50 p-6 md:p-8 mb-8 rounded-xl" data-testid="upgrade-card">
          <div className="flex items-start gap-3 mb-3">
            <Crown size={20} weight="fill" className="text-blue-600 mt-1" />
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                / Upgrade to Premium
              </span>
              <h3 className="font-bold text-xl mt-1 text-slate-900">Remove daily limits</h3>
            </div>
          </div>
          <ul className="text-sm text-slate-600 space-y-1 mb-5 ml-8 list-disc">
            <li>Unlimited content kit generations</li>
            <li>Unlimited AI image generations</li>
            <li>Priority queue when traffic is heavy</li>
            <li>Premium models — switch between Claude, GPT and Gemini freely</li>
          </ul>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 text-sm rounded-lg transition-colors"
            data-testid="upgrade-button"
          >
            See pricing <ArrowUpRight size={14} />
          </Link>
        </div>
      )}

      {/* Subscription card */}
      {sub && (
        <div className="border border-slate-200 bg-white p-6 md:p-8 mb-8 rounded-xl" data-testid="subscription-card">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                / Active subscription
              </span>
              <h3 className="font-bold text-xl mt-2 mb-1 text-slate-900">
                {sub.plan_id?.replace(/_/g, " ").toUpperCase()}
              </h3>
              <p className="text-sm text-slate-500">
                ${sub.amount?.toFixed(2)} {sub.currency?.toUpperCase()} · renews {new Date(sub.current_period_end).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={doCancel}
              disabled={cancelling}
              className="border border-slate-200 hover:bg-red-50 hover:border-red-200 text-slate-600 hover:text-red-500 px-4 py-2 text-xs uppercase tracking-wider transition-colors inline-flex items-center gap-2 rounded-lg"
              data-testid="cancel-subscription-button"
            >
              <X size={12} /> {cancelling ? "Cancelling…" : "Cancel"}
            </button>
          </div>
        </div>
      )}

      {/* Billing history */}
      {history.length > 0 && (
        <div className="border border-slate-200 bg-white mb-8 rounded-xl overflow-hidden" data-testid="billing-history">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
            <Receipt size={14} className="text-slate-500" />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              / Billing history
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="border-b border-slate-100 bg-slate-50">
              <tr className="text-left">
                <th className="px-6 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Date</th>
                <th className="px-6 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Plan</th>
                <th className="px-6 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Amount</th>
                <th className="px-6 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-b-0" data-testid={`history-row-${i}`}>
                  <td className="px-6 py-3 text-xs text-slate-500">{new Date(h.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-3 text-slate-700">{h.plan_id?.replace(/_/g, " ")}</td>
                  <td className="px-6 py-3 text-slate-700">${h.amount?.toFixed(2)} {h.currency?.toUpperCase()}</td>
                  <td className="px-6 py-3">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 border rounded ${
                      h.payment_status === "paid" ? "border-green-200 text-green-700 bg-green-50" :
                      h.payment_status === "pending" ? "border-yellow-200 text-yellow-700 bg-yellow-50" :
                      "border-slate-200 text-slate-500"
                    }`}>
                      {h.payment_status || "—"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form onSubmit={onSave} className="space-y-5" data-testid="profile-form">
        <div>
          <label className={labelClass}>
            <User size={12} /> Display name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
            data-testid="profile-name-input"
          />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>
              <Globe size={12} /> Country / nationality
            </label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className={inputClass}
              data-testid="profile-country-select"
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>
              <Clock size={12} /> Timezone
            </label>
            <select
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              className={inputClass}
              data-testid="profile-timezone-select"
            >
              {timezones.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-2">
              Article timestamps are displayed in this zone.
            </p>
          </div>
        </div>

        {err && (
          <div className="border border-red-200 bg-red-50 p-3 text-xs text-red-600 rounded-lg" data-testid="profile-error">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold px-6 py-3 text-sm rounded-lg transition-colors"
          data-testid="profile-save-button"
        >
          {busy ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}
